/**
 * AuthContext — authentication state for the entire app.
 *
 * Principle 1 (Single Source of Truth): one place owns auth state.
 * Principle 2 (No Silent Failure): a safety timeout guarantees loading
 *   never hangs forever if Supabase is unreachable.
 * Principle 3 (Clean Separation): this file only manages auth state —
 *   no routing, no UI, no API calls beyond auth itself.
 * Principle 4 (Real Login): users sign in via LoginPage — no auto-login.
 *   ProtectedRoute redirects unauthenticated users to /login.
 * Principle 5 (Admin flag): after login, `is_admin` is read from profiles
 *   so the admin panel can be gated without an extra backend round-trip.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Fetch the admin flag from the profiles table for the given user id.
  // Silent — a network failure just leaves isAdmin = false.
  const fetchAdminFlag = async (userId) => {
    try {
      const { data } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', userId)
        .single();
      setIsAdmin(data?.is_admin === true);
    } catch {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    // Safety net: if Supabase takes > 8 s, stop loading so UI doesn't hang.
    const safetyTimer = setTimeout(() => setLoading(false), 8000);

    // Mirror auth-state changes (initial session + subsequent sign-in/out)
    // into React state. onAuthStateChange fires immediately with the current
    // session (or null) on subscription setup, so no separate getSession()
    // call is needed.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      clearTimeout(safetyTimer);

      if (session?.user) {
        fetchAdminFlag(session.user.id);
      } else {
        setIsAdmin(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, []);

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { full_name: fullName } },
    });
    return { data, error };
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export { AuthContext };
